import React, { useEffect } from "react";
import { useParams, useLocation, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, CheckCircle2 } from "lucide-react";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import Contact from "@/components/site/Contact";
import WhatsAppFloat from "@/components/site/WhatsAppFloat";
import ParticleField from "@/components/site/ParticleField";
import CTABand from "@/components/site/CTABand";
import { AurixMark, HRLogoFull } from "@/components/site/Logo";
import {
  StudioIcon,
  MediaIcon,
  GrowthIcon,
  FmcgIcon,
} from "@/components/site/DivisionIcons";

const DIVISIONS = {
  "aurix-studio": {
    key: "studio",
    name: "AURIX STUDIO",
    tagline: "Build fast. Build smart.",
    intro: "Websites, apps, automation and digital experiences engineered to help businesses launch and grow quickly.",
    icon: StudioIcon,
    isFlagship: true,
    services: [
      { title: "Websites & Landing Pages", desc: "Bespoke React sites launched in 48 hours after design approval." },
      { title: "Mobile & Web Apps", desc: "iOS, Android and web apps that feel premium end-to-end." },
      { title: "Product & Dashboard UI", desc: "Enterprise dashboards and SaaS interfaces designed to convert." },
      { title: "AI Automations", desc: "Workflows that remove operational drag from your business." },
      { title: "Managed Hosting & CI/CD", desc: "Secure, monitored, always-on cloud infrastructure." },
      { title: "Maintenance & Support", desc: "Ongoing care so your product keeps compounding." },
    ],
    industries: ["Schools", "Colleges", "Cafés", "Restaurants", "Gyms", "Boutiques", "Healthcare", "Startups", "SMEs"],
    process: [
      { step: "Discovery", desc: "One call to align on goals, timeline and scope." },
      { step: "Design", desc: "Premium visual direction, iterated fast." },
      { step: "Build", desc: "Ship a live URL within 48 hours of design sign-off." },
      { step: "Grow", desc: "Iterate weekly on data, feedback and business signals." },
    ],
    whyUs: [
      "Enterprise-grade code from day one",
      "Delivery you can bet a launch on",
      "Direct access to senior engineers",
      "Design + build under one roof",
    ],
    seoTitle: "AURIX STUDIO — Websites & Apps by HR Enterprises",
    seoDescription: "Premium websites, mobile apps, product UI and AI automations built in 48 hours. Flagship technology division of HR ENTERPRISES.",
  },
  "aurix-media": {
    key: "media",
    name: "AURIX MEDIA",
    tagline: "Make every brand unforgettable.",
    intro: "Creative campaigns, branding, commercials and content that people remember.",
    icon: MediaIcon,
    services: [
      { title: "Brand Identity Systems", desc: "Logos, guidelines and design systems built to last a decade." },
      { title: "Ad Films & Commercials", desc: "Cinematic short-form and long-form video for every channel." },
      { title: "Social Content", desc: "Reels, static creative and campaigns that stop scrolls." },
      { title: "Product Photography", desc: "In-studio and on-location, tuned for e-commerce and PR." },
      { title: "Motion & Post-Production", desc: "Editing, VFX and sound design of theatrical quality." },
      { title: "Editorial & Copy", desc: "Copy and storytelling that reads premium." },
    ],
    industries: ["FMCG Brands", "Restaurants", "Boutiques", "Hospitality", "Real Estate", "Wellness", "D2C"],
    process: [
      { step: "Brief", desc: "Deep-dive on brand pillars and campaign objectives." },
      { step: "Concept", desc: "Multiple creative routes with mood boards and scripts." },
      { step: "Produce", desc: "In-studio and on-location production." },
      { step: "Launch", desc: "Multi-channel delivery with distribution support." },
    ],
    whyUs: [
      "Cinematic craft on every frame",
      "Award-caliber post-production",
      "Fast, decisive creative direction",
      "Integrated with AURIX GROWTH for distribution",
    ],
    seoTitle: "AURIX MEDIA — Brand, Content & Commercials by HR Enterprises",
    seoDescription: "Creative campaigns, brand identity, ad films and content that make businesses unforgettable. Content division of HR ENTERPRISES.",
  },
  "aurix-growth": {
    key: "growth",
    name: "AURIX GROWTH",
    tagline: "More leads. More growth.",
    intro: "B2B sales, digital marketing, business consulting and scalable growth strategies.",
    icon: GrowthIcon,
    services: [
      { title: "Performance Marketing", desc: "Paid media on Meta, Google, LinkedIn and native platforms." },
      { title: "SEO & Content Strategy", desc: "AI-assisted content engines that own the SERP." },
      { title: "B2B Outbound Sales", desc: "Trained SDR teams that book meetings on your behalf." },
      { title: "CRM & Lifecycle", desc: "Email, WhatsApp and nurture flows that convert." },
      { title: "Business Consulting", desc: "Positioning, pricing and unit-economics guidance." },
      { title: "Analytics & Attribution", desc: "Dashboards that tell you exactly what's working." },
    ],
    industries: ["Startups", "SMEs", "SaaS", "E-commerce", "Healthcare", "Real Estate", "Fintech"],
    process: [
      { step: "Audit", desc: "Deep-dive on funnel, offer, CAC and LTV." },
      { step: "Blueprint", desc: "90-day growth blueprint with KPIs and budget." },
      { step: "Execute", desc: "Weekly experiments across paid, SEO and sales." },
      { step: "Compound", desc: "Double-down on winners quarter after quarter." },
    ],
    whyUs: [
      "Revenue-linked KPIs, not vanity metrics",
      "Combines paid, SEO and outbound sales",
      "Transparent reporting on every rupee spent",
      "Consulting-grade strategic thinking",
    ],
    seoTitle: "AURIX GROWTH — B2B Sales & Digital Marketing by HR Enterprises",
    seoDescription: "Performance marketing, SEO, B2B sales and consulting engineered for revenue outcomes. Growth division of HR ENTERPRISES.",
  },
  "aurix-fmcg-labs": {
    key: "fmcg",
    name: "AURIX FMCG LABS",
    tagline: "Ideas into products.",
    intro: "Building innovative consumer products from concept to market, starting with AB ORGANIC JUICES.",
    icon: FmcgIcon,
    services: [
      { title: "R&D and Formulation", desc: "Ingredient sourcing, recipe development and iteration." },
      { title: "Packaging & Label Design", desc: "Premium packaging that stands out on shelf." },
      { title: "Manufacturing Partners", desc: "Vetted co-manufacturers across categories." },
      { title: "Distribution Planning", desc: "GT, MT and D2C route-to-market design." },
      { title: "Retail Go-To-Market", desc: "Launch plans, pricing and trade marketing." },
      { title: "In-House Brand", desc: "AB Organic Juices — our flagship consumer brand." },
    ],
    industries: ["Beverages", "Snacks", "Wellness", "Personal Care", "Home Essentials"],
    process: [
      { step: "Ideate", desc: "Category whitespaces and consumer insights." },
      { step: "Formulate", desc: "R&D, prototyping and consumer testing." },
      { step: "Package", desc: "Brand and packaging design that pops." },
      { step: "Launch", desc: "Retail rollout with distribution partners." },
    ],
    whyUs: [
      "In-house brand credentials (AB Organic Juices)",
      "End-to-end from formulation to shelf",
      "Consumer-first product thinking",
      "Backed by AURIX MEDIA + GROWTH for launch",
    ],
    seoTitle: "AURIX FMCG LABS — Consumer Products by HR Enterprises",
    seoDescription: "Building innovative FMCG products from concept to market, starting with AB Organic Juices. Consumer products division of HR ENTERPRISES.",
  },
};

const DivisionPage = () => {
  const { slug: paramSlug } = useParams();
  const location = useLocation();
  // Support both /aurix-studio and /divisions/aurix-studio route styles
  const slug = paramSlug || location.pathname.replace(/^\//, "").split("/").pop();
  const division = DIVISIONS[slug];

  useEffect(() => {
    if (!division) return;
    const prevTitle = document.title;
    document.title = division.seoTitle;
    const meta = document.querySelector('meta[name="description"]');
    const prevDesc = meta?.getAttribute("content");
    if (meta) meta.setAttribute("content", division.seoDescription);
    window.scrollTo(0, 0);
    return () => {
      document.title = prevTitle;
      if (meta && prevDesc) meta.setAttribute("content", prevDesc);
    };
  }, [division]);

  if (!division) return <Navigate to="/" replace />;
  const Icon = division.icon;

  return (
    <main
      className="relative min-h-screen bg-[#0a0a0a]"
      data-testid={`division-page-${division.key}`}
    >
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden bg-[#060606] pb-16 pt-32 lg:pb-24 lg:pt-40">
        <div className="aurix-aura" />
        <div className="absolute inset-0 opacity-70">
          <ParticleField density={50} />
        </div>
        <div className="relative z-10 mx-auto max-w-[1400px] px-6 lg:px-10">
          <Link
            to="/"
            className="mb-8 inline-flex items-center gap-2 text-[13px] text-zinc-400 transition-colors hover:text-[var(--gold-bright)]"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to HR ENTERPRISES
          </Link>

          <div className="grid grid-cols-12 items-center gap-8">
            <div className="col-span-12 lg:col-span-7">
              <div className="mb-5 flex items-center gap-3">
                <div className="grid h-12 w-12 place-items-center rounded-xl border border-[var(--gold)]/40 bg-[var(--gold)]/10 text-[var(--gold-bright)]">
                  {division.isFlagship ? (
                    <AurixMark size={38} className="rounded-md" />
                  ) : (
                    <Icon className="h-6 w-6" />
                  )}
                </div>
                <span className="font-mono-label rounded-full border border-white/10 bg-white/[0.03] px-3 py-1 text-zinc-400">
                  Division of HR ENTERPRISES
                </span>
                {division.isFlagship && (
                  <span className="font-mono-label rounded-full border border-[var(--gold)]/30 bg-[var(--gold)]/[0.06] px-3 py-1 text-[var(--gold-bright)]">
                    Flagship
                  </span>
                )}
              </div>
              <motion.h1
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: [0.2, 0.8, 0.2, 1] }}
                className="font-display text-5xl leading-[0.98] tracking-tight text-white sm:text-6xl lg:text-[80px]"
              >
                {division.name}
              </motion.h1>
              <p className="mt-5 font-serif-display text-[20px] italic text-zinc-200 sm:text-[22px]">
                {division.tagline}
              </p>
              <p className="mt-4 max-w-2xl text-[15.5px] leading-relaxed text-zinc-400">
                {division.intro}
              </p>
              <div className="mt-9 flex flex-wrap gap-3">
                <a href="#contact" className="btn-gold">
                  Start with {division.name.split(" ")[1]}
                  <ArrowUpRight className="h-4 w-4" strokeWidth={2.2} />
                </a>
                <Link to="/" className="btn-ghost">
                  Explore Other Divisions
                </Link>
              </div>
            </div>
            <div className="col-span-12 lg:col-span-5">
              <div className="relative mx-auto aspect-square w-full max-w-[380px]">
                <div className="absolute inset-0 rounded-full bg-[radial-gradient(circle_at_50%_50%,rgba(212,175,55,0.28),transparent_65%)] blur-2xl" />
                <motion.div
                  animate={{ y: [0, -10, 0] }}
                  transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                  className="relative flex h-full items-center justify-center"
                >
                  {division.isFlagship ? (
                    <AurixMark
                      size={280}
                      className="rounded-3xl border border-[var(--gold)]/25 shadow-[0_30px_80px_-30px_rgba(212,175,55,0.5)]"
                    />
                  ) : (
                    <div className="relative flex h-[70%] w-[70%] items-center justify-center rounded-3xl border border-[var(--gold)]/25 bg-[#0e0e10] text-[var(--gold-bright)] shadow-[0_30px_80px_-30px_rgba(212,175,55,0.5)]">
                      <Icon className="h-24 w-24" />
                    </div>
                  )}
                </motion.div>
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 60, repeat: Infinity, ease: "linear" }}
                  className="pointer-events-none absolute inset-0 rounded-full border border-[var(--gold)]/15"
                />
                <motion.div
                  animate={{ rotate: -360 }}
                  transition={{ duration: 90, repeat: Infinity, ease: "linear" }}
                  className="pointer-events-none absolute inset-8 rounded-full border border-[var(--gold)]/10"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="py-24 lg:py-32">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="mb-12">
            <div className="mb-4 flex items-center gap-3">
              <div className="h-px w-8 bg-[var(--gold)]/60" />
              <span className="font-mono-label text-[var(--gold)]">Capabilities</span>
            </div>
            <h2 className="font-display text-4xl leading-tight tracking-tight text-white sm:text-5xl">
              What we ship.
            </h2>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {division.services.map((s, idx) => (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: (idx % 3) * 0.06 }}
                className="rounded-2xl border border-white/[0.06] bg-[#0e0e10] p-6 transition-colors duration-500 hover:border-[var(--gold)]/25"
              >
                <div className="font-mono-label text-[var(--gold-bright)]">
                  {String(idx + 1).padStart(2, "0")}
                </div>
                <h3 className="mt-4 font-display text-xl font-semibold tracking-tight text-white">
                  {s.title}
                </h3>
                <p className="mt-2 text-[13.5px] leading-relaxed text-zinc-400">
                  {s.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* INDUSTRIES + PROCESS */}
      <section className="border-y border-white/[0.06] bg-[#08080a] py-24 lg:py-28">
        <div className="mx-auto grid max-w-[1400px] grid-cols-1 gap-16 px-6 lg:grid-cols-2 lg:px-10">
          <div>
            <div className="mb-4 flex items-center gap-3">
              <div className="h-px w-8 bg-[var(--gold)]/60" />
              <span className="font-mono-label text-[var(--gold)]">Industries served</span>
            </div>
            <h3 className="font-display text-3xl font-semibold tracking-tight text-white sm:text-4xl">
              Who we work with.
            </h3>
            <div className="mt-6 flex flex-wrap gap-2">
              {division.industries.map((ind) => (
                <span
                  key={ind}
                  className="rounded-full border border-white/10 bg-white/[0.02] px-3.5 py-1.5 text-[13px] text-zinc-300"
                >
                  {ind}
                </span>
              ))}
            </div>
          </div>
          <div>
            <div className="mb-4 flex items-center gap-3">
              <div className="h-px w-8 bg-[var(--gold)]/60" />
              <span className="font-mono-label text-[var(--gold)]">How we ship</span>
            </div>
            <h3 className="font-display text-3xl font-semibold tracking-tight text-white sm:text-4xl">
              Our process.
            </h3>
            <ol className="mt-6 space-y-3">
              {division.process.map((p, i) => (
                <li
                  key={p.step}
                  className="flex items-start gap-4 rounded-xl border border-white/[0.06] bg-[#0e0e10] p-4"
                >
                  <div className="grid h-8 w-8 shrink-0 place-items-center rounded-full border border-[var(--gold)]/30 bg-[var(--gold)]/10 text-[11px] font-medium text-[var(--gold-bright)]">
                    {i + 1}
                  </div>
                  <div>
                    <div className="font-display text-[15px] font-semibold text-white">
                      {p.step}
                    </div>
                    <div className="mt-1 text-[13px] text-zinc-400">
                      {p.desc}
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="py-24 lg:py-28">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="mb-10">
            <div className="mb-4 flex items-center gap-3">
              <div className="h-px w-8 bg-[var(--gold)]/60" />
              <span className="font-mono-label text-[var(--gold)]">Why choose us</span>
            </div>
            <h2 className="font-display text-4xl leading-tight tracking-tight text-white sm:text-5xl">
              Why {division.name.split(" ")[1]}?
            </h2>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {division.whyUs.map((w) => (
              <div
                key={w}
                className="flex items-start gap-3 rounded-xl border border-white/[0.06] bg-[#0e0e10] p-5"
              >
                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-[var(--gold-bright)]" strokeWidth={1.8} />
                <span className="text-[14.5px] text-zinc-200">{w}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CTABand />
      <Contact />
      <Footer />
      <WhatsAppFloat />
    </main>
  );
};

export default DivisionPage;
